import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import BreadCumb from '../../components/common/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import backArrow from "../../assets/task/back_arrow.svg";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import InputsNew from '../../components/common/InputsNew';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import SearchableInputsDropdown from '../../components/common/SearchableDropdown';
import InputsDropdownMultipleSearchSelect from '../../components/common/InputsDropdownMultipleSearchSelect';
import { useLocation, useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '../../components/common/ToastNotification';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_PROJECT } from '../../api/mutation';
import Loader from '../../components/common/Loader';
import { EMPLOYEES_BY_AGENCY_ID } from '../../api/query';
import Popup from '../../components/dashboard/Popup';
import PopupForMessage from '../../components/common/PopupForMessage';
import { uploadFileDirectly } from './AddProject';

const projectStatusOptions = [
    {
        key: '1',
        name: 'Active'
    },
    {
        key: '2',
        name: 'Archive'
    }
]

// const projectMemberOptions = [
//     {
//         key: '11',
//         name: 'varshil',
//         type: 'Developer'
//     },
//     {
//         key: '12',
//         name: 'Prateek',
//         type: 'Developer'
//     }
// ]


// Validation schema
const addProjectSchema = yup.object().shape({
    projectName: yup.string().required('This field is required'),
    status: yup.string().required('This field is required'),
    statusID: yup.string().required('This field is required'),
    projectTagLine: yup.string().required('This field is required'),
    // projectMember: yup.string().required('This field is required'),
    // projectMemberID: yup.string().required('This field is required'),
    projectDescription: yup.string()
        .test('not-only-whitespace', 'Please add a valid Project Description', value => !value || value.trim().length > 0).required('This field is required'),
    projectNiche: yup.string()
        .test('not-only-whitespace', 'Please add a valid Project Niche', value => !value || value.trim().length > 0).nullable(),
    idealCustomerProfile: yup.string()
        .test('not-only-whitespace', 'Please add a valid Ideal CustomerProfile', value => !value || value.trim().length > 0).nullable(),
});

const EditProject: FunctionComponent = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [imgUrl, setImgUrl] = useState("");
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(
        null
    );
    const location = useLocation()
    const navigate = useNavigate();
    const { userInfo } = useAuth();

    const employeeName = `${userInfo?.first_name} ${userInfo?.last_name}`
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const [showMembers, setShowMembers] = useState(false)

    const state = location.state as {
        projectID: any,
        projectName: any,
        projectStatusID: any,
        projectTagLine: any,
        projectNiche: any,
        projectIdealCustomerProfile: any,
        projectUniqueAboutIt: any,
        projectMembers: any[],
        projectCreatedBy: any,
        projectImgUrl: any
    };

    const {
        projectID,
        projectName,
        projectStatusID,
        projectTagLine,
        projectNiche,
        projectIdealCustomerProfile,
        projectUniqueAboutIt,
        projectMembers,
        projectCreatedBy,
        projectImgUrl
    } = state

    const [projectMemberOptions, setProjectMemberOptions] = useState<
        { key: string; name: string }[]
    >([]);
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);

    //api
    const [createProject, { loading: createProjectLoading }] = useMutation(CREATE_PROJECT, {
        fetchPolicy: 'no-cache',
    })

    const [
        getEmployeesByAgencyID,
        { data: employeesData, loading: employeesDataLoading },
    ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<any>({
        resolver: yupResolver(addProjectSchema)
    });

    //useEffects
    useEffect(() => {
        if (organizationID) {
            getEmployeesByAgencyID({
                variables: {
                    agency_id: parseInt(organizationID)
                }
            });
        }
    }, [organizationID])

    useEffect(() => {
        if (employeesData && employeesData.employees_by_agency_id) {
            const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
                key: emp.employee_id,
                name: `${emp.first_name?.trim()} ${emp.last_name?.trim()}`
            }));
            setProjectMemberOptions(employeeOptions);
        }
    }, [employeesData])

    useEffect(() => {
        if (state) {
            setValue('projectName', projectName);
            setValue('status', projectStatusID ? projectStatusOptions.find(option => option.key == projectStatusID)?.name : '');
            setValue('statusID', projectStatusID);
            setValue('projectTagLine', projectTagLine);
            setValue('projectNiche', projectNiche || '');
            setValue('idealCustomerProfile', projectIdealCustomerProfile || '');
            setValue('projectDescription', projectUniqueAboutIt);
            setImgUrl(projectImgUrl || '')

            // Set selected members
            if (projectMembers && Array.isArray(projectMembers)) {
                const members = projectMembers.map((member: any) => ({
                    key: String(member.employee_id), // Convert to string to match dropdown options
                    name: member.member_name,
                }));

                setSelectedMembers(members);
            }
        }

    }, [state])

    //watch
    const watchStatus: string = watch('status');

    function handleSelectStatus(key: number, name: string): void {
        setValue('status', name)
        setValue('statusID', key)
        trigger('status')
    }

    const handleSelectMembers = (key: any, name: any, type: any) => {
        const allOptions = projectMemberOptions;

        if (key === "all") {
            if (name === true) {
                // Deselect all
                setSelectedMembers([]);
            } else {
                // Select all
                setSelectedMembers(allOptions);
            }
        } else {
            setSelectedMembers((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    // REMOVE if already selected
                    return prev.filter((item) => item.key != key);
                } else {
                    // ADD if not selected
                    return [...prev, { key, name, type }];
                }
            });
        }
        // } else {
        //     const exists = selectedMembers.find((item) => item.key == key);
        //     if (exists) {
        //         setSelectedMembers((prev) => prev.filter((item) => item.key != key));
        //     } else {
        //         setSelectedMembers((prev) => [...prev, { key, name, type }]);
        //     }
        // }
    };

    function handleIconClick() {
        fileInputRef.current?.click();
    }

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        // if (!["image/jpeg", "image/png"].includes(file.type)) {
        //     setValidationMessage("Only JPG and PNG files are allowed.");
        //     return;
        // }

        setValidationMessage(null);
        setIsUploading(true);

        try {
            const token = localStorage.getItem("authToken")!;
            const url = await uploadFileDirectly(file, file.name, token);
            setImgUrl(url);
            setValue("imageurl", url);
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setIsUploading(false);
        }
    }

    // const handleRemoveMember = (key: string) => {
    //     setSelectedMembers((prev) => prev.filter((item) => item.key !== key));
    // };

    async function onSubmit(data: any): Promise<void> {
        if (selectedMembers.length === 0) {
            showError('Please select at least one project member');
            return;
        }

        const projectMembers = selectedMembers.map((member: any) => ({
            project_id: parseInt(projectID),
            member_id: parseInt(member.key),
        }));

        const projectInput = {
            id: parseInt(projectID),
            name: data.projectName,
            status_id: parseInt(data.statusID),
            tagline: data.projectTagLine,
            niche: data?.projectNiche ? data.projectNiche : null,
            ideal_customer_profile: data?.idealCustomerProfile ? data.idealCustomerProfile : null,
            unique_about_it: data.projectDescription,
            project_members: projectMembers,
            created_by: parseInt(projectCreatedBy),
            updated_by: parseInt(employeeID),
            organization_id: parseInt(organizationID),
            image_url: imgUrl ? imgUrl : projectImgUrl
        }

        try {
            const response = await createProject({ variables: { input: projectInput } });
            if (response.data.create_project.status_code === 200) {
                showSuccess(response.data.create_project.message)
                navigate(-1)
            }
            if (response.data.create_project.status_code === 201) {
                showSuccess(response.data.create_project.message)
                navigate(-1)
            }
            if (response.data.create_project.status_code === 400) {
                showError(response.data.create_project.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form id="cts-respon-p-dv" className="w-full relative  flex flex-row items-start justify-center flex-wrap content-start py-40 px-20  mq1490:px-[30px]  mq575:px-5  box-border text-left text-base text-gray font-manrope min-h-screen"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] ">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Edit Project</h3>
                    </div>
                    <div className="self-stretch rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-8 gap-6">
                        <b className="self-stretch relative text-lg">Project Details</b>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <InputsNew
                                contentPlaceholder="Project Name"
                                label="Project Name*"
                                type="text"
                                register={register('projectName')}
                                errorMessage={errors.projectName}
                            // defaultValue={projectName || ''}
                            />
                            <div className="flex-1 flex flex-col items-start justify-start gap-2 max-w-[412px]">
                                <SearchableInputsDropdown
                                    label="Status*"
                                    initialValue={watchStatus}
                                    placeHolder="Select Project Status"
                                    options={projectStatusOptions}
                                    onSelect={handleSelectStatus}
                                    errorMessage={errors?.status}
                                />
                            </div>
                            <InputsNew
                                contentPlaceholder="Project Tagline"
                                label="Project Tagline*"
                                type="text"
                                register={register('projectTagLine')}
                                errorMessage={errors.projectTagLine}
                            />
                            <div className="flex-1 flex flex-col items-start justify-start gap-2 max-w-[412px]">
                                <InputsDropdownMultipleSearchSelect
                                    label="Project Members*"
                                    placeHolder="Choose"
                                    options={projectMemberOptions}
                                    onSelect={handleSelectMembers}
                                    inputType="checkbox"
                                    checkedItems={selectedMembers}
                                />

                                <div className="reportmulselect-blg  cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                    <p
                                        className='cursor-pointer' onClick={() => setShowMembers(true)}
                                    >
                                        View Members ({selectedMembers?.length})
                                    </p>
                                    {/* <div className="inner-reportmulselect-blg  items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg w-full">
                                        {selectedMembers.map((it: any) => (
                                            <div
                                                key={it}
                                                className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                            >
                                                <div className="flex flex-row items-center justify-start">
                                                    <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                                        {it.name}
                                                    </div>
                                                </div>
                                                <img
                                                    className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                    loading="lazy"
                                                    alt="Remove"
                                                    src={closeDowpDowniconimg}
                                                    onClick={() => handleRemoveMember(it.key)}
                                                />
                                            </div>
                                        ))}
                                    </div> */}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <div className=" rounded flex flex-col items-center justify-start  text-left mq1275:justify-start w-full">
                                <label className="self-stretch relative font-semibold dark:text-white pb-2">Project Logo upload</label>
                                <div
                                    // className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex flex-col items-center justify-start box-border max-w-[100%] min-w-[100%] mq950:min-w-full text-left agencyinfo-img-blg mq950:p-0`} id="calltype-row "
                                    className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex items-center justify-start box-border max-w-[100%] min-w-[100%] mq950:min-w-full text-left agencyinfo-img-blg mq950:p-0`} id="calltype-row "

                                >
                                    {isUploading ? (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[33px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <div className="text-main-blue">Uploading...</div>
                                            </div>
                                        </div>
                                    ) : imgUrl ? (
                                        <div className="bg-white p-2 shadow-\[0px_1px_2px_rgba\(16\2c _24\2c _40\2c _0\.05\)\] border-surface-border   inner_upload_img_box onborading-bdr-blg">
                                            <img
                                                className="min-w-36 h-[100px] relative overflow-hidden shrink-0 cursor-pointer imgagency-upload"
                                                loading="lazy"
                                                alt=""
                                                src={imgUrl}
                                                onClick={handleIconClick}
                                            />
                                        </div>
                                    ) : (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-start py-[35px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <img
                                                    className="w-6 h-6 relative overflow-hidden shrink-0 dark-white-img"
                                                    loading="lazy"
                                                    alt=""
                                                // src={icon}
                                                />
                                                <p className="self-stretch relative capitalize my-0">
                                                    Agency Logo Upload
                                                </p>
                                                <div
                                                    className="cursor-pointer rounded bg-main-blue flex flex-row items-center justify-center p-1.5"
                                                    onClick={handleIconClick}
                                                >
                                                    <img
                                                        className="h-5 w-5 relative"
                                                        loading="lazy"
                                                        alt=""
                                                    // src={icon_1}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                    accept=".jpg,.jpeg,.png"
                                // accept="image/*"
                                />
                                {validationMessage && (
                                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                        <img src={''} alt="" />
                                        {validationMessage}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Unique Description"
                                    label="What is unique about it"
                                    type="textarea"
                                    register={register('projectDescription')}
                                    errorMessage={errors.projectDescription}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-row items-start justify-start gap-6">
                            <InputsNew
                                contentPlaceholder="Project Niche"
                                label="Project Niche"
                                type="text"
                                register={register('projectNiche')}
                                errorMessage={errors.projectNiche}
                            />
                            <InputsNew
                                contentPlaceholder="Ideal Customer Profile"
                                label="Ideal Customer Profile"
                                type="text"
                                register={register('idealCustomerProfile')}
                                errorMessage={errors.idealCustomerProfile}
                            />
                        </div>
                        <div className="self-stretch flex flex-row flex-wrap items-center justify-end py-0 pr-0 gap-[8px] mq450:box-border mq950:box-border mq1425:box-border min-w-full">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
            {showMembers && (
                <PopupForMessage
                    viewMembersOnly={true}
                    isOpen={showMembers}
                    onClose={() => setShowMembers(false)}
                    showMessage={selectedMembers.map((it: any) => (
                        <div
                            key={it}
                            className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                        >
                            <div className="flex flex-row items-center justify-start">
                                <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                    {it.name}
                                </div>
                            </div>
                            {/* <img
                                className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                loading="lazy"
                                alt="Remove"
                                src={closeDowpDowniconimg}
                                onClick={() => handleRemoveMember(it.key)}
                            /> */}
                        </div>
                    ))}
                />
            )}
            {(createProjectLoading || employeesDataLoading) && (
                <Loader />
            )}
        </>
    );
};

export default EditProject;
